import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {loadingTablesAC, setErrorAC} from "./authReducer";
import {AxiosError} from "axios";
import {darkSoresApi} from "../api/darkStoresApi";
import {endDS, startDS} from "../common/optionsForDate";
import {DarkStoreType} from "../common/types";
import {titleDSChart} from "../common/darkStoreOptions";

type InitialStateType = {
    darkStoreData: any     // количество дарксторов в зависимости от количества товаров
    darkStoreWeeks: any // недели для первого графика
    top: DarkStoreType[] | null  // топ 10
    allStores: DarkStoreType[] | null // полные данные для вывода дарксторов
    globalIndicators: DarkStoreType[] | null
    date: string[]
    city: string,
    globDS: {
        count: number
        middle: number
        active: number
    } | null

}

export const slice = createSlice({
    name: 'darkstore',
    initialState: {
        darkStoreData: [],  // по количеству товаров
        darkStoreWeeks: [], // недели
        top: [],
        allStores: null,      // полные данные
        globalIndicators: null,      // данные для последних трех графиков
        date: [startDS, endDS],
        city: 'Москва',
        globDS: null

    } as InitialStateType,
    reducers: {
        changeDateDSAC(state, action) {
            state.date = action.payload
        },
        changeCityDSAC(state, action) {
            state.city = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDarkStoreDataTop.fulfilled, (state, action) => {
                state.top = action.payload
            })
            .addCase(fetchDataDS.fulfilled, (state, action) => {
                if (action.payload.length > 1) {
                    state.darkStoreData = action.payload[0]
                    state.darkStoreWeeks = action.payload[1]
                    state.allStores = action.payload[2]
                } else {
                    state.globalIndicators = action.payload[0]
                }
            })
            .addCase(fetchGlobDS.fulfilled, (state, action) => {
                state.globDS = action.payload
            })

    }
})

export const fetchGlobDS = createAsyncThunk('fetchGlobDS', async (param: {
    start_date_from: string,
    end_date_to: string,
}, {dispatch, rejectWithValue}) => {
    try {
        let offset = 0; // Начинаем с offset = 0
        const limit = 1000; // Лимит данных за один запрос
        let allResults: any[] = []; // Массив для хранения всех данных

        // Повторяем запрос, пока не получим все данные
        while (true) {
            const res = await darkSoresApi.getDarkStore('', param.start_date_from, param.end_date_to, offset);
            const results = res.data.results;

            allResults = allResults.concat(results); // Добавляем данные в общий массив

            // Если количество данных меньше лимита, это последняя порция, завершаем цикл
            if (results.length < limit) {
                break;
            }

            offset += limit; // Увеличиваем offset на limit
        }
        const changedResults =  allResults.sort((a,b) => {
            if (a.week_number > b.week_number) return -1
            if (a.week_number < b.week_number) return 1
            return 0
        }).filter((el) => el.week_number === allResults[0].week_number)
        let sum = 0
        let active = 0
        changedResults.forEach((el) => {
            sum += el.kol_vo_tovarov
            if (el.status_aktivnost === 'Активна') active++
        })
        const count = changedResults.length
        const middle = Math.round(sum / count)


        return {count, middle, active}

    } catch (err) {
        dispatch(loadingTablesAC('failed'));
        const error = err as AxiosError;

        if (!error.response) {
            throw err;
        }
        dispatch(setErrorAC(error.response.status));
        return rejectWithValue(error.response);
    }

})

export const fetchDataDS = createAsyncThunk('fetchDataDS', async (param: {
    city: string,
    start_date_from: string,
    end_date_to: string,
    offset: number,
    type: 'page' | 'global'
}, {dispatch, rejectWithValue}) => {

    try {
        const {city, start_date_from, end_date_to} = param;
        let offset = 0; // Начинаем с offset = 0
        const limit = 1000; // Лимит данных за один запрос
        let allResults: any[] = []; // Массив для хранения всех данных

        // Повторяем запрос, пока не получим все данные
        while (true) {
            const res = await darkSoresApi.getDarkStore(city, start_date_from, end_date_to, offset);
            const results = res.data.results;
            console.log(res.data)
            allResults = allResults.concat(results); // Добавляем данные в общий массив

            // Если количество данных меньше лимита, это последняя порция, завершаем цикл
            if (results.length < limit) {
                break;
            }

            offset += limit; // Увеличиваем offset на limit
        }

        // Обработка всех данных
        const names = [[0, 2999], [3000, 3999], [4000, 4999], [5000, 5999], [6000, 20000]];
        const result: any[] = [];

        // формирование недель
        const weeks: string[] = [];
        allResults.forEach((el: any) => (
            !weeks.includes(el.week_number) ? weeks.push(el.week_number) : el
        ));
        weeks.sort();


        // формирование данных для первого графика
        names.forEach((item) => {
            const stores = weeks.map(() => 0); // Инициализируем массив нулями для каждой недели
            allResults.forEach((el: any) => {
                if (el.kol_vo_tovarov > item[0] && el.kol_vo_tovarov <= item[1]) {
                    const ind = weeks.indexOf(el.week_number);
                    stores[ind] += 1; // Увеличиваем счетчик для соответствующей недели
                }
            });
            const key = item[0]; // Просто берем значение
            if (key in titleDSChart) {  // Проверяем, есть ли такой ключ в title
                result.push({
                    name: titleDSChart[key as keyof typeof titleDSChart], // Безопасное использование
                    type: 'column',
                    data: stores
                });
            } else {
                console.warn(`Invalid key: ${key}`); // Защита от ошибок
            }
        });

        if (param.type === 'global') {
            return [allResults]
        }
        return [result, weeks, allResults]

    } catch (err) {
        dispatch(loadingTablesAC('failed'));
        const error = err as AxiosError;

        if (!error.response) {
            throw err;
        }
        dispatch(setErrorAC(error.response.status));
        return rejectWithValue(error.response);
    }
});


export const fetchDarkStoreDataTop = createAsyncThunk('darkStoreDataTop', async (param: {
    city: string,
    start_date_from: string,
    end_date_to: string
}, {dispatch, rejectWithValue}) => {

    try {
        const {city, start_date_from, end_date_to} = param
        const res = await darkSoresApi.getDarkStoreTopData(city, start_date_from, end_date_to)
        return res.data.results
    } catch (err) {
        dispatch(loadingTablesAC('failed'))
        const error = err as AxiosError

        if (!error.response) {
            throw err
        }
        dispatch(setErrorAC(error.response.status))
        return rejectWithValue(error.response)

    }
})

export const {changeDateDSAC, changeCityDSAC} = slice.actions
export const darkstoreReducer = slice.reducer